import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import { VideoEmbed } from "@/components/VideoEmbed";

/**
 * "Vidéo mise en avant" — utilisé sur /langage-de-lumiere en bas de page ET
 * sur la home dans le bloc "Découverte". Embed du video.featured=true,
 * fallback sur la dernière vidéo YouTube.
 */
export function FeaturedVideoBlock({ variant = "hero", eyebrow, title, description }) {
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        http.get("/videos/featured")
            .then(({ data }) => setVideo(data))
            .catch(() => setVideo(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !video) return null;

    if (variant === "hero") {
        return (
            <section className="border-t border-border/40" data-testid="featured-video-hero">
                <div className="max-w-5xl mx-auto px-6 py-20 md:py-28">
                    <div className="text-xs tracking-[0.35em] uppercase text-primary mb-4 font-medium flex items-center gap-2">
                        <Play className="w-3.5 h-3.5" /> {eyebrow || "Vidéo · Transmission"}
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl mb-6 leading-tight">
                        {title || "Recevoir une transmission en vidéo"}
                    </h2>
                    {description && (
                        <p className="text-lg text-foreground/70 mb-10 max-w-2xl leading-relaxed">
                            {description}
                        </p>
                    )}
                    <div className="rounded-3xl overflow-hidden shadow-lg">
                        <VideoEmbed video={video} />
                    </div>
                    <div className="pt-6 flex items-center gap-4">
                        <Link
                            to={`/transmissions/${video.slug}`}
                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                            data-testid="featured-video-cta"
                        >
                            Lire la transcription et la description complète <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/transmissions"
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            Toutes les transmissions →
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    // Compact card variant (for homepage sidebar-ish placement)
    return (
        <Link to={`/transmissions/${video.slug}`} className="block group rounded-3xl overflow-hidden bg-card border border-border/60 hover:border-primary/40 transition-colors">
            <div className="aspect-video relative overflow-hidden bg-black">
                {video.thumbnail_url && (
                    <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-90">
                    <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center">
                        <Play className="w-7 h-7 text-primary translate-x-0.5" fill="currentColor" />
                    </div>
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-serif text-lg leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                    {video.title}
                </h3>
            </div>
        </Link>
    );
}
